import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';
import { ButtonSearch, useResponsive } from '@garnish/core';

import { LocationsMapSearchAreaButton } from './components/LocationsMapSearchAreaButton';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationsMapSearchArea = (props: SearchAreaProps) => {
  const { disabled, isLoading, onPress, rightInset, topInset } = props;

  const { match } = useResponsive();
  const { formatMessage } = useIntl();

  const insetStyles = useStyle(() => {
    if (rightInset === undefined && topInset === undefined) return;

    return {
      ...(rightInset === undefined ? {} : { right: rightInset }),
      ...(topInset === undefined ? {} : { top: topInset }),
    };
  }, [rightInset, topInset]);

  const wrapperStyles = [styles.wrapper, match([undefined, styles.wrapperSM]), insetStyles];
  const searchAreaLabel = formatMessage(messages.locationMapSearchArea);

  return (
    <View style={wrapperStyles} pointerEvents="box-none">
      <LocationsMapSearchAreaButton
        accessibilityLabel={searchAreaLabel}
        isLoading={isLoading}
        disabled={disabled}
        onPress={onPress}
      >
        {searchAreaLabel}
      </LocationsMapSearchAreaButton>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    ...StyleSheet.absoluteFill,
    bottom: 'auto',
    zIndex: 1,
    flexDirection: 'row',
    paddingTop: theme.spacing['4'],
    paddingHorizontal: theme.spacing['4'],
  },
  wrapperSM: {
    justifyContent: 'center',
    paddingTop: theme.spacing['6'],
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  locationMapSearchArea: {
    defaultMessage: 'Search area',
    description: 'Location | Map Search Area',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type SearchAreaProps = {
  rightInset?: number;
  topInset?: number;
} & Pick<React.ComponentProps<typeof ButtonSearch>, 'onPress' | 'isLoading' | 'disabled'>;
